import React, { useState } from 'react';
import propTypes from 'prop-types';
import { timeDay, timeFormat } from 'd3';

import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormLabel from '@material-ui/core/FormLabel';
import RadioGroup from '@material-ui/core/RadioGroup';
import Radio from '@material-ui/core/Radio';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core';
import {
  getRawData,
  filterData,
  formatData,
  downloadData,
  getLabel,
  ENERGY_SOURCES,
  ZONES,
  PROVINCES,
  TIME_SCALES,
} from '../helpers/download';

const optionify = (map) => {
  return Object.values(map).map((value) => {
    return {
      value,
      label: getLabel(value),
    };
  });
};

const styles = (theme) => ({
  header: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  formContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: 24,
    '&:first-child': {
      paddingTop: 32,
    },
  },
  formContentCentered: {
    alignItems: 'center',
  },
  datesRow: {
    display: 'flex',
    gap: '12px',
  },
});

const useStyles = makeStyles(styles);

const INITIAL_FILTERS = {
  source: ENERGY_SOURCES.solar,
  zone: ZONES.region,
  timeScale: TIME_SCALES.hourly,
  province: 0,
};

const formatDate = timeFormat('%Y-%m-%d');

const getInitialFilters = () => {
  const startDate = formatDate(new Date().getTime());
  const endDate = formatDate(new Date(timeDay.offset(new Date(), 1)));
  return {
    ...INITIAL_FILTERS,
    startDate,
    endDate,
  };
};
export default function DownloadDialog({ toggleClose, isOpen, data }) {
  const classes = useStyles();
  const initialFilters = getInitialFilters();
  const [filters, setFilters] = useState(initialFilters);
  const [isLoading, setIsLoading] = useState(false);

  // sources
  const energySourceOptions = optionify(ENERGY_SOURCES);

  const setSource = (e) => {
    const source = e.target.value;
    const newFilters = {
      source,
    };
    if (source === ENERGY_SOURCES.wind_offshore) {
      newFilters.zone = ZONES.offshore;
    }
    setFilters({ ...filters, ...newFilters });
  };

  // zones
  const zoneOptions = optionify(ZONES);

  const setZone = (e) => {
    const zone = e.target.value;
    const newFilters = { zone };
    if (zone === ZONES.province && !filters.province) {
      newFilters.province = Object.values(PROVINCES)[0];
    }
    setFilters({ ...filters, ...newFilters });
  };

  // Province
  const provinceOptions = Object.entries(PROVINCES).map(([key, value]) => {
    return {
      value,
      label: getLabel(key),
    };
  });
  const setProvince = (e) => {
    const province = parseInt(e.target.value, 10);
    setFilters({ ...filters, province });
  };

  // time scale
  const timeScaleOptions = optionify(TIME_SCALES);

  const setTimeScale = (e) => {
    const timeScale = e.target.value;
    setFilters({ ...filters, timeScale });
  };

  const MIN_DATE = '2021-01-01'; // earliest realtime data

  // start date
  const setStartDate = (e) => {
    const newDate = e.target.value;
    const startDate = newDate > MIN_DATE ? newDate : MIN_DATE;
    const endDate = filters.endDate > startDate ? filters.endDate : startDate;
    setFilters({ ...filters, startDate, endDate });
  };

  //end date
  const setEndDate = (e) => {
    const newDate = e.target.value;
    const endDate = newDate > MIN_DATE ? newDate : MIN_DATE;
    const startDate =
      endDate > filters.startDate ? filters.startDate : startDate;
    setFilters({ ...filters, endDate, startDate });
  };

  const downloadValues = async (event) => {
    event.preventDefault();
    try {
      setIsLoading(true);
      const rawData = await getRawData(filters);
      const filteredData = filterData(rawData, filters);
      const formattedData = formatData(filteredData, filters);
      downloadData(formattedData, filters);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={toggleClose}
      maxWidth={false}
      // fullWidth
      aria-labelledby="download-dialog-title"
    >
      <DialogTitle
        id="download-dialog-title"
        classes={{ root: classes.header }}
      >
        Download
      </DialogTitle>
      {isLoading ? (
        <DialogContent
          className={`${classes.formContent} ${classes.formContentCentered}`}
        >
          <CircularProgress></CircularProgress>
        </DialogContent>
      ) : (
        <form onSubmit={downloadValues} noValidate autoComplete="off">
          <DialogContent
            classes={{
              root: classes.formContent,
            }}
          >
            <div>
              <FormControl>
                <FormLabel component="legend">Energiebron</FormLabel>
                <RadioGroup
                  aria-label="energy-source"
                  name="source"
                  value={filters.source}
                  onChange={setSource}
                  row
                >
                  {energySourceOptions.map(({ label, value }) => {
                    return (
                      <FormControlLabel
                        labelPlacement="top"
                        key={value}
                        value={value}
                        control={<Radio />}
                        label={label}
                      ></FormControlLabel>
                    );
                  })}
                </RadioGroup>
              </FormControl>
            </div>
            <div>
              <FormControl>
                <FormLabel component="legend">Regio</FormLabel>
                <RadioGroup
                  aria-label="region"
                  name="region"
                  value={filters.zone}
                  onChange={setZone}
                  row
                >
                  {filters.source === ENERGY_SOURCES.wind_offshore ? (
                    <FormControlLabel
                      labelPlacement="top"
                      key={ZONES.offshore}
                      value={ZONES.offshore}
                      control={<Radio />}
                      label={getLabel(ZONES.offshore)}
                      disabled
                    ></FormControlLabel>
                  ) : (
                    zoneOptions
                      .filter(({ value }) => value !== ZONES.offshore)
                      .map(({ label, value }) => {
                        return (
                          <FormControlLabel
                            labelPlacement="top"
                            key={value}
                            value={value}
                            control={<Radio />}
                            label={label}
                          ></FormControlLabel>
                        );
                      })
                  )}
                </RadioGroup>
              </FormControl>
            </div>
            {filters.zone !== ZONES.province ? null : (
              <div>
                <FormControl>
                  <FormLabel component="legend">Provincie</FormLabel>
                  <RadioGroup
                    aria-label="province"
                    name="province"
                    value={filters.province}
                    onChange={setProvince}
                    row
                  >
                    {provinceOptions.map(({ label, value }) => {
                      return (
                        <FormControlLabel
                          labelPlacement="top"
                          key={value}
                          value={value}
                          control={<Radio />}
                          label={label}
                        ></FormControlLabel>
                      );
                    })}
                  </RadioGroup>
                </FormControl>
              </div>
            )}
            <div>
              <TextField
                label="Tijdgranulariteit"
                select
                id="time-scale"
                value={filters.timeScale}
                onChange={setTimeScale}
              >
                {timeScaleOptions.map(({ label, value }) => {
                  return (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  );
                })}
              </TextField>
            </div>
            <div className={classes.datesRow}>
              <TextField
                label="Startdatum"
                type="date"
                min={MIN_DATE}
                id="start-date"
                value={filters.startDate}
                onChange={setStartDate}
              ></TextField>
              <TextField
                label="Einddatum"
                type="date"
                id="end-date"
                min={MIN_DATE}
                value={filters.endDate}
                onChange={setEndDate}
              ></TextField>
            </div>
          </DialogContent>
          <DialogActions>
            <Button type="button" onClick={toggleClose} color="primary">
              Annuleer
            </Button>
            <Button type="submit" color="primary" variant="contained">
              Download
            </Button>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
}

DownloadDialog.propTypes = {
  toggleClose: propTypes.func,
  isOpen: propTypes.bool,
  initialDate: propTypes.string,
};
